import {
	useEffect,
	useMemo,
	useRef,
	useState,
	type Dispatch,
	type MouseEvent as ReactMouseEvent,
	type PointerEvent as ReactPointerEvent,
	type SetStateAction,
} from 'react';
import { Vector3, type PerspectiveCamera } from 'three';

import { normalizeGraphLayout, type AutoConnection, type GraphDocument, type GraphNodeAnchor } from '../../../../core/entities/graph-document';
import type { VisibleRelationshipEdge } from '../../../../core/policies/explicit-relationships';
import type { GraphDisplayConnection, GraphDisplayConnectionEmphasis } from '../../../../core/policies/graph-display';
import type { NodeType } from '../../../../core/entities/node';
import {
	beginNodePlacementSession,
	cancelNodePlacementSession,
	commitNodePlacementSession,
	updateNodePlacementSession,
	type NodePlacementSession,
} from '../../../../core/policies/workspace/node-placement-session';
import type { WorkspaceMotionProfile } from '../../../../core/policies/workspace/visual-theme-profile';
import { resolveSceneInteractionMode, type SceneInteractionMode } from '../../../../core/policies/workspace/scene-interaction-mode';
import { visibleNodes } from '../../../../core/policies/search/focus-visualization';
import { useLocale } from '../../../preferences/i18n/LocaleProvider';
import { GraphAppearancePanel } from '../../components/GraphAppearancePanel';
import {
	applyGraphCamera3DView,
	createGraphCamera3DView,
	dampGraphCamera3DView,
	decayFlyVelocity,
	fitGraphCamera3DViewToAnchors,
	flyGraphCamera3DView,
	focusGraphCamera3DView,
	isGraphCamera3DViewSettled,
	isFlyVelocityActive,
	orbitGraphCamera3DView,
	panGraphCamera3DView,
	zoomGraphCamera3DView,
} from '../../services/graph-camera-3d';
import {
	createScene3DRendererState,
	resizeScene3DRendererState,
	setScene3DRenderSnapshot,
	worldPointFromAnchor,
} from './Scene3DRenderer';
import {
	createScene3DDepthRange,
	createScene3DNodePresentation,
	type SceneNodeHaloStrength,
} from './Scene3DNodePresentation';
import {
	graphNodeTypeVisualControls,
	mixHexColors,
	presetForNodeType,
	synchronizeGraphNodeTypeVisualConfig,
	toRgba,
	updateGraphNodeTypeBrightness,
	updateGraphNodeTypeColor,
	updateGraphNodeTypeLabelsVisible,
	type GraphNodeTypeVisualConfig,
} from '../../services/graph-appearance-config';

interface ProjectedNode {
	nodeId: string;
	displayName: string;
	type: NodeType;
	x: number;
	y: number;
	labelX: number;
	labelY: number;
	radius: number;
	opacity: number;
	labelOpacity: number;
	labelScale: number;
	cameraDistance: number;
	depth: number;
	anchor: GraphNodeAnchor;
	clipZ: number;
	showLabel: boolean;
	showTypeTag: boolean;
	haloStrength: SceneNodeHaloStrength;
	isFocused: boolean;
	visualColorHex: string;
	visualBrightness: number;
}

interface ProjectedConnection {
	connectionId: string;
	reasonCode: string;
	source: ProjectedNode;
	target: ProjectedNode;
	highlighted: boolean;
	emphasis: GraphDisplayConnectionEmphasis;
}

interface ProjectedRelationshipEdge {
	pairId: string;
	source: ProjectedNode;
	target: ProjectedNode;
	summaryText: string;
	edgeKind: VisibleRelationshipEdge['summary']['edgeKind'];
	hasOverflow: boolean;
	labelX: number;
	labelY: number;
}

interface PlacementGuide {
	nodeId: string;
	displayName: string;
	startX: number;
	startY: number;
	currentX: number;
	currentY: number;
	currentRadius: number;
	depthValue: number;
	deltaX: number;
	deltaY: number;
	deltaZ: number;
}

interface NumericRange {
	minimum: number;
	maximum: number;
}

interface SceneLabelBounds {
	left: number;
	right: number;
	top: number;
	bottom: number;
}

interface SceneNodeTypeRef {
	type: string;
}

export function GraphScene3D({
	graph,
	visibleType,
	focusedNodeId,
	highlightedConnections,
	displayConnections,
	visibleRelationshipEdges = [],
	interactionMode = 'navigate',
	motionProfile = 'default',
	onFocus,
	onCommitNodePlacement = () => undefined,
	onInteractionModeChange = () => undefined,
	onResetOverview,
	readonlyMode = false,
	themeProfileId = 'dark-cosmic-editorial',
	appearancePanelOpen,
	onAppearancePanelOpenChange,
	typeVisualConfig,
	onTypeVisualConfigChange,
}: {
	graph: GraphDocument;
	visibleType: NodeType | 'All';
	focusedNodeId: string | null;
	highlightedConnections: AutoConnection[];
	displayConnections?: GraphDisplayConnection[];
	visibleRelationshipEdges?: VisibleRelationshipEdge[];
	interactionMode?: SceneInteractionMode;
	motionProfile?: WorkspaceMotionProfile;
	onFocus: (nodeId: string | null) => void;
	onCommitNodePlacement?: (nodeId: string, deltaX: number, deltaY: number, deltaZ: number) => void;
	onInteractionModeChange?: (mode: SceneInteractionMode) => void;
	onResetOverview: () => void;
	readonlyMode?: boolean;
	themeProfileId?: string;
	appearancePanelOpen?: boolean;
	onAppearancePanelOpenChange?: Dispatch<SetStateAction<boolean>>;
	typeVisualConfig?: Record<string, GraphNodeTypeVisualConfig>;
	onTypeVisualConfigChange?: Dispatch<SetStateAction<Record<string, GraphNodeTypeVisualConfig>>>;
}) {
	const { t } = useLocale();
	const resolvedInteractionMode = resolveSceneInteractionMode(interactionMode, { readonlyMode });
	const sectionRef = useRef<HTMLElement>(null);
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const suppressSceneClickRef = useRef(false);
	const navigateDragRef = useRef<{
		pointerId: number;
		lastClientX: number;
		lastClientY: number;
		lastDeltaX: number;
		lastDeltaY: number;
		moved: boolean;
		interaction: 'pan' | 'orbit';
	} | null>(null);
	const [placementSession, setPlacementSession] = useState<NodePlacementSession | null>(null);
	const [cameraView, setCameraView] = useState(() => createGraphCamera3DView());
	const [cameraTargetView, setCameraTargetView] = useState(() => createGraphCamera3DView());
	const [internalTypeVisualConfig, setInternalTypeVisualConfig] = useState<Record<string, GraphNodeTypeVisualConfig>>(() => ({}));
	const [internalAppearancePanelOpen, setInternalAppearancePanelOpen] = useState(false);
	const cameraViewRef = useRef(cameraView);
	const cameraTargetRef = useRef(cameraTargetView);
	const flyFrameRef = useRef<number | null>(null);
	const pressedFlyKeysRef = useRef<Set<FlythroughKey>>(new Set());
	const driftTimeRef = useRef(0);
	const driftFrameRef = useRef<number | null>(null);
	const [driftTime, setDriftTime] = useState(0);
	const resolvedTypeVisualConfig = typeVisualConfig ?? internalTypeVisualConfig;
	const setResolvedTypeVisualConfig = onTypeVisualConfigChange ?? setInternalTypeVisualConfig;
	const resolvedAppearancePanelOpen = appearancePanelOpen ?? internalAppearancePanelOpen;
	const setResolvedAppearancePanelOpen = onAppearancePanelOpenChange ?? setInternalAppearancePanelOpen;
	const [viewport, setViewport] = useState(() => ({
		width: typeof window === 'undefined' ? 1280 : window.innerWidth,
		height: typeof window === 'undefined' ? 720 : window.innerHeight,
	}));

	useEffect(() => {
		cameraViewRef.current = cameraView;
	}, [cameraView]);

	useEffect(() => {
		cameraTargetRef.current = cameraTargetView;
	}, [cameraTargetView]);

	useEffect(() => {
		if (typeVisualConfig) {
			return;
		}

		setInternalTypeVisualConfig((currentConfig) => synchronizeGraphNodeTypeVisualConfig(graph.nodes, currentConfig, t));
	}, [graph.nodes, t, typeVisualConfig]);

	useEffect(() => {
		return () => {
			if (flyFrameRef.current !== null) {
				cancelAnimationFrame(flyFrameRef.current);
			}
		};
	}, []);

	useEffect(() => {
		if (motionProfile === 'reduced') {
			return;
		}

		let startTime: number | null = null;

		const tick = (timestamp: number) => {
			if (startTime === null) {
				startTime = timestamp;
			}

			const elapsed = timestamp - startTime;
			driftTimeRef.current = elapsed;
			setDriftTime(elapsed);
			driftFrameRef.current = requestAnimationFrame(tick);
		};

		driftFrameRef.current = requestAnimationFrame(tick);

		return () => {
			if (driftFrameRef.current !== null) {
				cancelAnimationFrame(driftFrameRef.current);
				driftFrameRef.current = null;
			}
		};
	}, [motionProfile]);

	useEffect(() => {
		if (isGraphCamera3DViewSettled(cameraViewRef.current, cameraTargetView)) {
			return;
		}

		let frameId: number | null = null;

		const tick = () => {
			setCameraView((currentView) => {
				const targetView = cameraTargetRef.current;
				if (isGraphCamera3DViewSettled(currentView, targetView)) {
					return targetView;
				}

				const nextView = dampGraphCamera3DView(currentView, targetView);
				if (!isGraphCamera3DViewSettled(nextView, targetView)) {
					frameId = requestAnimationFrame(tick);
					return nextView;
				}

				return targetView;
			});
		};

		frameId = requestAnimationFrame(tick);

		return () => {
			if (frameId !== null) {
				cancelAnimationFrame(frameId);
			}
		};
	}, [cameraTargetView]);

	useEffect(() => {
		const section = sectionRef.current;
		if (!section) {
			return;
		}

		const updateViewport = () => {
			const bounds = section.getBoundingClientRect();
			setViewport({
				width: Math.max(1, Math.round(bounds.width || window.innerWidth || 1280)),
				height: Math.max(1, Math.round(bounds.height || window.innerHeight || 720)),
			});
		};

		updateViewport();

		if (typeof ResizeObserver === 'undefined') {
			window.addEventListener('resize', updateViewport);
			return () => window.removeEventListener('resize', updateViewport);
		}

		const observer = new ResizeObserver(updateViewport);
		observer.observe(section);

		return () => observer.disconnect();
	}, []);

	function handleCanvasClick(event: ReactMouseEvent<HTMLCanvasElement>) {
		if (suppressSceneClickRef.current) {
			suppressSceneClickRef.current = false;
			return;
		}

		const canvasBounds = canvasRef.current?.getBoundingClientRect();
		if (canvasBounds) {
			const hitNode = pickProjectedNode(scene.nodes, event.clientX - canvasBounds.left, event.clientY - canvasBounds.top);
			if (hitNode) {
				onFocus(hitNode.nodeId);
				return;
			}
		}

		handleResetOverview();
	}

	useEffect(() => {
		if (resolvedInteractionMode === 'move-node') {
			return;
		}

		setPlacementSession((session) => (session?.status === 'draft' ? cancelNodePlacementSession(session) : session));
	}, [resolvedInteractionMode]);

	useEffect(() => {
		if (!focusedNodeId) {
			return;
		}

		const nodes = visibleNodes(graph.nodes, visibleType);
		const anchors = normalizeGraphLayout(graph.layout).nodeAnchors;
		const focusedIndex = Math.max(0, nodes.findIndex((node) => node.nodeId === focusedNodeId));
		const focusedNode = nodes.find((node) => node.nodeId === focusedNodeId);
		if (!focusedNode) {
			return;
		}

		const anchor = anchors[focusedNodeId] ?? fallbackAnchor(focusedIndex, nodes.length);
		setCameraTargetView((view) => focusGraphCamera3DView(view, {
			worldX: anchor.x,
			worldY: -anchor.y,
			worldZ: anchor.z,
		}));
	}, [focusedNodeId, graph.layout, graph.nodes, visibleType]);

	useEffect(() => {
		if (!placementSession || placementSession.status !== 'draft') {
			return;
		}

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key !== 'Escape') {
				return;
			}

			suppressSceneClickRef.current = true;
			setPlacementSession(null);
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [placementSession]);

	const scene = useMemo(() => {
		const nodes = visibleNodes(graph.nodes, visibleType);
		const synchronizedConfig = synchronizeGraphNodeTypeVisualConfig(nodes, resolvedTypeVisualConfig, t);
		const anchors = normalizeGraphLayout(graph.layout).nodeAnchors;
		const draftAnchors = placementSession?.status === 'draft'
			? { [placementSession.nodeId]: placementSession.draftAnchor }
			: {};
		const emphasizedNodeIds = new Set<string>();
		for (const connection of highlightedConnections) {
			emphasizedNodeIds.add(connection.sourceNodeId);
			emphasizedNodeIds.add(connection.targetNodeId);
		}

		const timeSec = driftTime / 1000;
		const source = nodes.map((node, index) => {
			const baseAnchor = draftAnchors[node.nodeId] ?? anchors[node.nodeId] ?? fallbackAnchor(index, nodes.length);
			const driftedAnchor = applyAnchorDrift(baseAnchor, node.nodeId, index, timeSec);
			return {
				nodeId: node.nodeId,
				displayName: node.displayName,
				type: node.type,
				anchor: driftedAnchor,
				baseAnchor,
				isFocused: node.nodeId === focusedNodeId,
				isNeighbor: emphasizedNodeIds.has(node.nodeId),
			};
		});
		const depthRange = createScene3DDepthRange(source.map((node) => node.anchor));
		const renderer = setScene3DRenderSnapshot(
			resizeScene3DRendererState(createScene3DRendererState(), viewport),
			{
				focusedNodeId,
				nodes: source.map((node) => ({
					nodeId: node.nodeId,
					anchor: node.anchor,
					presentation: createScene3DNodePresentation(node, depthRange),
				})),
			},
		);
		applyGraphCamera3DView(renderer.camera, cameraView);

		const sourceByNodeId = Object.fromEntries(source.map((node) => [node.nodeId, node]));
		const projectedNodes = renderer.instances
			.map<ProjectedNode>((instance) => {
				const sourceNode = sourceByNodeId[instance.nodeId];
				const projected = instance.position.clone().project(renderer.camera);
				const depthProgress = clamp((projected.z + 1) / 2, 0, 1);
				const radius = Math.max(10, Math.round(instance.presentation.radiusPx * clamp(1.45 - depthProgress * 0.75, 0.58, 1.36)));
				const isFocused = instance.nodeId === focusedNodeId;
				const baseOpacity = isFocused ? 1.0 : 0.6;

				return {
					nodeId: instance.nodeId,
					displayName: sourceNode.displayName,
					type: sourceNode.type,
					x: (projected.x * 0.5 + 0.5) * renderer.viewport.width,
					y: (-projected.y * 0.5 + 0.5) * renderer.viewport.height,
					labelX: (projected.x * 0.5 + 0.5) * renderer.viewport.width,
					labelY: (-projected.y * 0.5 + 0.5) * renderer.viewport.height + radius + 18,
					radius,
					opacity: baseOpacity * clamp(1.04 - depthProgress * 0.42, 0.58, 1),
					labelOpacity: clamp(1.04 - depthProgress * 0.42, 0.38, 1),
					labelScale: 1,
					cameraDistance: renderer.camera.position.distanceTo(instance.position),
					depth: depthProgress,
					anchor: sourceNode.baseAnchor,
					clipZ: projected.z,
					showLabel: instance.presentation.showLabel || isFocused,
					showTypeTag: instance.presentation.showTypeTag || isFocused,
					haloStrength: instance.presentation.haloStrength,
					isFocused,
					visualColorHex: synchronizedConfig[sourceNode.type]?.colorHex ?? presetForNodeType(sourceNode.type).colorHex,
					visualBrightness: synchronizedConfig[sourceNode.type]?.brightness ?? presetForNodeType(sourceNode.type).brightness,
				};
			})
			.filter((node) => Number.isFinite(node.x) && Number.isFinite(node.y))
			.sort((left, right) => left.depth - right.depth);

		const projectedMap = Object.fromEntries(projectedNodes.map((node) => [node.nodeId, node]));
		const highlightedIds = new Set(highlightedConnections.map((connection) => connection.connectionId));
		const sourceConnections = displayConnections ?? graph.connections.map((connection) => ({ ...connection, emphasis: 'normal' as const }));
		const projectedConnections = sourceConnections
			.filter((connection) => projectedMap[connection.sourceNodeId] && projectedMap[connection.targetNodeId])
			.map<ProjectedConnection>((connection) => ({
				...connection,
				source: projectedMap[connection.sourceNodeId],
				target: projectedMap[connection.targetNodeId],
				highlighted: connection.emphasis === 'focused' || highlightedIds.has(connection.connectionId),
			}));
		const projectedRelationshipEdges = visibleRelationshipEdges
			.filter((edge) => projectedMap[edge.sourceNodeId] && projectedMap[edge.targetNodeId])
			.map<ProjectedRelationshipEdge>((edge) => {
				const source = projectedMap[edge.sourceNodeId];
				const target = projectedMap[edge.targetNodeId];
				const deltaX = target.x - source.x;
				const deltaY = target.y - source.y;
				const length = Math.hypot(deltaX, deltaY) || 1;
				const normalX = (-deltaY / length) * 14;
				const normalY = (deltaX / length) * 14;
				const midpointX = (source.x + target.x) / 2;
				const midpointY = (source.y + target.y) / 2;

				return {
					pairId: edge.relationshipPair.pairId,
					source,
					target,
					summaryText: edge.summary.summaryText,
					edgeKind: edge.summary.edgeKind,
					hasOverflow: edge.summary.hasOverflow,
					labelX: midpointX + normalX,
					labelY: midpointY + normalY,
				};
			})
			.filter((edge) => isPointInViewport(edge.labelX, edge.labelY, renderer.viewport));
		const labelCandidateNodes = projectedNodes.filter(
			(node) => isTypeLabelCandidate(node, synchronizedConfig) && isProjectedLabelInViewport(node, renderer.viewport),
		);
		const cameraDistanceRange = createNumericRange(labelCandidateNodes.map((node) => node.cameraDistance));
		const normalizedNodes = projectedNodes.map((node) => ({
			...node,
			labelScale: labelScaleForDistance(node.cameraDistance, cameraDistanceRange, node.isFocused),
			labelOpacity: labelOpacityForDistance(node.cameraDistance, cameraDistanceRange, node.isFocused),
		}));
		const visibleLabelNodeIds = selectVisibleSceneLabelNodeIds(normalizedNodes, renderer.viewport, synchronizedConfig);
		const placementGuide = placementSession?.status === 'draft'
			? createPlacementGuide(placementSession, renderer.camera, renderer.viewport, projectedMap)
			: null;

		return {
			typeControls: graphNodeTypeVisualControls(nodes, synchronizedConfig),
			relationshipEdges: projectedRelationshipEdges,
			nodes: normalizedNodes.map((node) => ({
				...node,
				showLabel: visibleLabelNodeIds.has(node.nodeId),
				showTypeTag: false,
			})),
			connections: projectedConnections,
			camera: renderer.camera,
			anchors: source.map((node) => node.baseAnchor),
			placementGuide,
		};
	}, [cameraView, displayConnections, driftTime, focusedNodeId, graph.connections, graph.layout, graph.nodes, highlightedConnections, placementSession, resolvedTypeVisualConfig, t, viewport, visibleRelationshipEdges, visibleType]);

	const overviewCameraView = useMemo(
		() => fitGraphCamera3DViewToAnchors(cameraTargetView, scene.anchors, viewport),
		[cameraTargetView, scene.anchors, viewport],
	);
	const focusedPlacementNode = scene.nodes.find((node) => node.nodeId === focusedNodeId) ?? null;

	function handleResetOverview() {
		setCameraTargetView(overviewCameraView);
		onResetOverview();
	}

	function startPlacementSessionForNode(node: ProjectedNode, pointerId: number | null, captureTarget?: HTMLCanvasElement) {
		const nextSession = beginNodePlacementSession(placementSession, {
			mode: resolvedInteractionMode,
			readonlyMode,
			nodeId: node.nodeId,
			anchor: node.anchor,
			planeZ: node.anchor.z,
			startedAt: new Date().toISOString(),
			pointerId,
		});
		if (!nextSession) {
			return false;
		}

		onFocus(node.nodeId);
		suppressSceneClickRef.current = true;
		if (captureTarget && pointerId !== null) {
			setPointerCaptureSafely(captureTarget, pointerId);
		}
		setPlacementSession(nextSession);
		return true;
	}

	useEffect(() => {
		const canvas = canvasRef.current;
		const context = getCanvas2DContext(canvas);
		if (!canvas || !context) {
			return;
		}

		const devicePixelRatio = typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1;
		canvas.width = Math.max(1, Math.round(viewport.width * devicePixelRatio));
		canvas.height = Math.max(1, Math.round(viewport.height * devicePixelRatio));
		canvas.style.width = `${viewport.width}px`;
		canvas.style.height = `${viewport.height}px`;
		context.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
		context.clearRect(0, 0, viewport.width, viewport.height);

		for (const connection of scene.connections) {
			drawSceneConnection(context, connection);
		}

		for (const relationshipEdge of scene.relationshipEdges) {
			drawSceneRelationshipEdge(context, relationshipEdge);
		}

		for (const node of scene.nodes) {
			drawSceneNode(context, node);
		}

		drawPlacementGuide(context, scene.placementGuide);
	}, [scene.connections, scene.nodes, scene.placementGuide, scene.relationshipEdges, viewport]);

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) {
			return;
		}

		const handleWheel = (event: WheelEvent) => {
			event.preventDefault();
			setCameraTargetView((view) => zoomGraphCamera3DView(view, event.deltaY));
		};

		canvas.addEventListener('wheel', handleWheel, { passive: false });

		return () => {
			canvas.removeEventListener('wheel', handleWheel);
		};
	}, []);

	useEffect(() => {
		if (resolvedInteractionMode !== 'navigate') {
			pressedFlyKeysRef.current.clear();
			stopFlyLoop();
			return;
		}

		const tick = () => {
			const movement = resolveFlythroughMovement(pressedFlyKeysRef.current);

			if (movement) {
				if (!isKeyboardFlyBlocked()) {
					setCameraTargetView((view) => flyGraphCamera3DView(view, movement));
				}
			} else {
				// No keys pressed → apply friction deceleration for glide-to-stop
				setCameraTargetView((view) => {
					if (!isFlyVelocityActive(view)) {
						return view;
					}
					return decayFlyVelocity(view);
				});
			}

			// Keep loop alive as long as there are pressed keys OR residual velocity
			const shouldContinue = pressedFlyKeysRef.current.size > 0;
			if (!shouldContinue) {
				// Check if velocity is still active (read from the latest target state)
				const latestTarget = cameraTargetRef.current;
				if (!isFlyVelocityActive(latestTarget)) {
					flyFrameRef.current = null;
					return;
				}
			}

			flyFrameRef.current = requestAnimationFrame(tick);
		};

		const startFlyLoop = () => {
			if (flyFrameRef.current !== null) {
				return;
			}

			flyFrameRef.current = requestAnimationFrame(tick);
		};

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.metaKey || event.ctrlKey || event.altKey) {
				return;
			}

			const key = normalizeFlythroughKey(event.key);
			if (!key || isKeyboardFlyBlocked()) {
				return;
			}

			event.preventDefault();
			pressedFlyKeysRef.current.add(key);
			startFlyLoop();
		};

		const handleKeyUp = (event: KeyboardEvent) => {
			const key = normalizeFlythroughKey(event.key);
			if (!key) {
				return;
			}

			pressedFlyKeysRef.current.delete(key);
			// Don't stop the loop — let inertia coast to a stop
			startFlyLoop();
		};

		const clearFlythrough = () => {
			pressedFlyKeysRef.current.clear();
			// Don't immediately stop — let velocity decay naturally
			startFlyLoop();
		};

		window.addEventListener('keydown', handleKeyDown);
		window.addEventListener('keyup', handleKeyUp);
		window.addEventListener('blur', clearFlythrough);

		return () => {
			window.removeEventListener('keydown', handleKeyDown);
			window.removeEventListener('keyup', handleKeyUp);
			window.removeEventListener('blur', clearFlythrough);
			pressedFlyKeysRef.current.clear();
			stopFlyLoop();
		};
	}, [resolvedInteractionMode]);

	function handleCanvasPointerDown(event: ReactPointerEvent<HTMLCanvasElement>) {
		const pointerButton = normalizePointerButton(event.button);

		if (resolvedInteractionMode === 'navigate') {
			if (pointerButton !== 0 && pointerButton !== 1) {
				return;
			}

			blurActiveElementSafely();

			if (pointerButton === 1) {
				event.preventDefault();
			}

			navigateDragRef.current = {
				pointerId: event.pointerId,
				lastClientX: event.clientX,
				lastClientY: event.clientY,
				lastDeltaX: 0,
				lastDeltaY: 0,
				moved: false,
				interaction: pointerButton === 1 || event.shiftKey ? 'orbit' : 'pan',
			};
			setPointerCaptureSafely(event.currentTarget, event.pointerId);
			return;
		}

		if (pointerButton !== 0) {
			return;
		}

		if (resolvedInteractionMode !== 'move-node' || readonlyMode) {
			return;
		}

		const canvas = event.currentTarget;
		const rect = canvas.getBoundingClientRect();
		const localX = event.clientX - rect.left;
		const localY = event.clientY - rect.top;
		const hitNode = pickProjectedNode(scene.nodes, localX, localY)
			?? pickFocusedPlacementNode(scene.nodes, focusedNodeId, localX, localY);
		if (!hitNode) {
			return;
		}

		startPlacementSessionForNode(hitNode, event.pointerId, canvas);
	}

	function handleCanvasPointerMove(event: ReactPointerEvent<HTMLCanvasElement>) {
		const navigateDrag = navigateDragRef.current;
		if (resolvedInteractionMode === 'navigate' && navigateDrag && navigateDrag.pointerId === event.pointerId) {
			const deltaX = event.clientX - navigateDrag.lastClientX;
			const deltaY = event.clientY - navigateDrag.lastClientY;

			if (deltaX !== 0 || deltaY !== 0) {
				navigateDragRef.current = {
					...navigateDrag,
					lastClientX: event.clientX,
					lastClientY: event.clientY,
					lastDeltaX: deltaX,
					lastDeltaY: deltaY,
					moved: true,
				};
				suppressSceneClickRef.current = true;
				if (navigateDrag.interaction === 'orbit') {
					setCameraTargetView((view) => orbitGraphCamera3DView(view, {
						deltaYaw: (deltaX / Math.max(360, viewport.width)) * Math.PI * 1.18,
						deltaPitch: (-deltaY / Math.max(240, viewport.height)) * Math.PI * 0.86,
					}));
				} else {
					setCameraTargetView((view) => panGraphCamera3DView(view, deltaX, deltaY, viewport));
				}
			}

			return;
		}

		if (!placementSession || placementSession.status !== 'draft') {
			return;
		}

		if (placementSession.pointerId !== null && placementSession.pointerId !== event.pointerId) {
			return;
		}

		const activeNode = scene.nodes.find((node) => node.nodeId === placementSession.nodeId);
		if (!activeNode) {
			return;
		}

		const rect = event.currentTarget.getBoundingClientRect();
		const draftAnchor = anchorFromScreenPoint(
			event.clientX - rect.left,
			event.clientY - rect.top,
			scene.camera,
			viewport,
			placementSession.startAnchor,
		);

		setPlacementSession((session) => (
			session && session.status === 'draft'
				? updateNodePlacementSession(session, draftAnchor)
				: session
		));
	}

	function handleCanvasPointerUp(event: ReactPointerEvent<HTMLCanvasElement>) {
		const navigateDrag = navigateDragRef.current;
		if (resolvedInteractionMode === 'navigate' && navigateDrag && navigateDrag.pointerId === event.pointerId) {
			if (navigateDrag.interaction === 'orbit' && navigateDrag.moved) {
				setCameraTargetView((view) => orbitGraphCamera3DView(view, {
					deltaYaw: (navigateDrag.lastDeltaX / Math.max(360, viewport.width)) * Math.PI * 0.78,
					deltaPitch: (-navigateDrag.lastDeltaY / Math.max(240, viewport.height)) * Math.PI * 0.52,
				}));
			}

			releasePointerCaptureSafely(event.currentTarget, event.pointerId);
			suppressSceneClickRef.current = navigateDrag.moved;
			navigateDragRef.current = null;
			return;
		}

		if (!placementSession || placementSession.status !== 'draft') {
			return;
		}

		if (placementSession.pointerId !== null && placementSession.pointerId !== event.pointerId) {
			return;
		}

		releasePointerCaptureSafely(event.currentTarget, event.pointerId);
		suppressSceneClickRef.current = true;
		const activeNode = scene.nodes.find((node) => node.nodeId === placementSession.nodeId);
		const rect = event.currentTarget.getBoundingClientRect();
		const finalAnchor = activeNode
			? anchorFromScreenPoint(
				event.clientX - rect.left,
				event.clientY - rect.top,
				scene.camera,
				viewport,
				placementSession.startAnchor,
			)
			: placementSession.draftAnchor;
		const committedSession = commitNodePlacementSession(updateNodePlacementSession(placementSession, finalAnchor));
		const deltaX = committedSession.draftAnchor.x - committedSession.startAnchor.x;
		const deltaY = committedSession.draftAnchor.y - committedSession.startAnchor.y;
		const deltaZ = committedSession.draftAnchor.z - committedSession.startAnchor.z;

		if (deltaX !== 0 || deltaY !== 0 || deltaZ !== 0) {
			onCommitNodePlacement(committedSession.nodeId, deltaX, deltaY, deltaZ);
		}

		setPlacementSession(null);
	}

	function handleCanvasPointerCancel(event: ReactPointerEvent<HTMLCanvasElement>) {
		if (navigateDragRef.current?.pointerId === event.pointerId) {
			releasePointerCaptureSafely(event.currentTarget, event.pointerId);
			navigateDragRef.current = null;
			return;
		}

		if (!placementSession || placementSession.status !== 'draft') {
			return;
		}

		releasePointerCaptureSafely(event.currentTarget, event.pointerId);
		suppressSceneClickRef.current = true;
		setPlacementSession((session) => (session ? cancelNodePlacementSession(session) : session));
		setPlacementSession(null);
	}

	function setPointerCaptureSafely(element: HTMLCanvasElement, pointerId: number) {
		try {
			element.setPointerCapture?.(pointerId);
		} catch {
			// Some automation paths and input devices do not expose an active pointer capture target.
		}
	}

	function releasePointerCaptureSafely(element: HTMLCanvasElement, pointerId: number) {
		try {
			element.releasePointerCapture?.(pointerId);
		} catch {
			// Ignore capture release failures when the browser never granted capture.
		}
	}

	function handleCanvasMouseDown(event: ReactMouseEvent<HTMLCanvasElement>) {
		blurActiveElementSafely();

		const pointerButton = normalizePointerButton(event.button);
		if (pointerButton === 1) {
			event.preventDefault();
		}
	}

	function stopFlyLoop() {
		if (flyFrameRef.current !== null) {
			cancelAnimationFrame(flyFrameRef.current);
			flyFrameRef.current = null;
		}
	}

	return (
		<section
			aria-label={t('workspace.mode.3dLabel')}
			className="graph-scene-3d"
			data-interaction-mode={resolvedInteractionMode}
			data-motion-profile={motionProfile}
			data-theme-profile={themeProfileId}
			ref={sectionRef}
		>
			<div aria-hidden="true" className="graph-scene-3d__atmosphere graph-scene-3d__atmosphere--primary" />
			<div aria-hidden="true" className="graph-scene-3d__atmosphere graph-scene-3d__atmosphere--secondary" />
			<div className="graph-scene-3d__backdrop" />
			<canvas
				aria-hidden="true"
				className="graph-scene-3d__canvas"
				data-testid="graph-scene-3d-canvas"
				onClick={handleCanvasClick}
				onMouseDown={handleCanvasMouseDown}
				onPointerCancel={handleCanvasPointerCancel}
				onPointerDown={handleCanvasPointerDown}
				onPointerMove={handleCanvasPointerMove}
				onPointerUp={handleCanvasPointerUp}
				ref={canvasRef}
			/>
			<div className="graph-scene-3d__label-layer">
				{scene.relationshipEdges.map((edge) => (
					<div
						aria-hidden="true"
						className={edge.edgeKind === 'mixed' ? 'graph-scene-3d__relationship-label is-mixed' : 'graph-scene-3d__relationship-label'}
						data-pair-id={edge.pairId}
						key={edge.pairId}
						style={{
							left: `${edge.labelX}px`,
							top: `${edge.labelY}px`,
							opacity: edge.hasOverflow ? 0.96 : 0.9,
						}}
					>
						<strong>{edge.summaryText}</strong>
					</div>
				))}
				{scene.nodes
					.filter((node) => node.showLabel)
					.map((node) => (
						<button
							aria-hidden="true"
							className={node.isFocused ? 'graph-scene-3d__label is-focused' : 'graph-scene-3d__label'}
							data-node-id={node.nodeId}
							data-node-type={node.type}
							data-node-radius={String(node.radius)}
							data-node-x={String(node.x)}
							data-node-y={String(node.y)}
							data-label-scale={String(node.labelScale)}
							key={node.nodeId}
							onClick={() => onFocus(node.nodeId)}
							style={{
								left: `${node.labelX}px`,
								top: `${node.labelY}px`,
								borderColor: toRgba(node.visualColorHex, 0.26),
								background: `linear-gradient(180deg, ${toRgba(node.visualColorHex, 0.16)}, rgba(5, 11, 24, .68))`,
								boxShadow: `0 12px 28px ${toRgba(node.visualColorHex, 0.18)}`,
								opacity: node.labelOpacity,
								transform: `translate(-50%, -50%) scale(${node.labelScale})`,
							}}
							tabIndex={-1}
							type="button"
						>
							<strong>{node.displayName}</strong>
						</button>
					))}
			</div>
			<nav aria-label={t('workspace.mode.3dNodeList')} className="graph-scene-3d__a11y-nav sr-only">
				{scene.nodes.map((node) => (
					<button key={node.nodeId} onClick={() => onFocus(node.nodeId)} type="button">
						{`${node.type} ${node.displayName}`}
					</button>
				))}
				{resolvedInteractionMode === 'move-node' && focusedPlacementNode ? (
					<button onClick={() => startPlacementSessionForNode(focusedPlacementNode, null)} type="button">
						{formatMessage(t('workspace.mode.3dBeginFocusedPlacement'), { name: focusedPlacementNode.displayName })}
					</button>
				) : null}
			</nav>
			<div className="graph-scene-3d__chrome">
				<section className="graph-scene-3d__summary-card">
					<p className="graph-scene-3d__eyebrow">{t('workspace.mode.3d')}</p>
					<h2>{t('workspace.mode.3dLabel')}</h2>
					<p className="hud-hint">{t('workspace.mode.3dHint')}</p>
					<div className="graph-scene-3d__meta" role="list" aria-label={t('workspace.mode.3dLabelsHint')}>
						<span className="graph-scene-3d__meta-chip" role="listitem">
							{formatMessage(t('workspace.mode.3dVisibleCount'), { count: String(scene.nodes.length) })}
						</span>
						<span className="graph-scene-3d__meta-chip" role="listitem">
							{formatMessage(t('workspace.mode.3dLabelTypesVisible'), {
								count: String(scene.typeControls.filter((control) => control.labelsVisible).length),
							})}
						</span>
					</div>
					<p className="hud-hint graph-scene-3d__overview-hint">{t('workspace.mode.3dLabelsHint')}</p>
					<p className="hud-hint graph-scene-3d__overview-hint">{t('workspace.mode.3dOverviewHint')}</p>
				</section>
				<section className="graph-scene-3d__control-dock" data-testid="graph-scene-3d-control-dock">
					<div className="graph-scene-3d__controls">
						<div aria-label={t('workspace.mode.3dTools')} className="graph-scene-3d__tools" role="group">
						<button
							aria-pressed={resolvedInteractionMode === 'navigate'}
							className="hud-chip graph-scene-3d__tool"
							onClick={() => onInteractionModeChange('navigate')}
							type="button"
						>
							{t('workspace.mode.3dNavigate')}
						</button>
						<button
							aria-pressed={resolvedInteractionMode === 'move-node'}
							className="hud-chip graph-scene-3d__tool"
							disabled={readonlyMode}
							onClick={() => onInteractionModeChange('move-node')}
							title={readonlyMode ? t('workspace.mode.3dMoveNodeReadonly') : undefined}
							type="button"
						>
							{t('workspace.mode.3dMoveNode')}
						</button>
					</div>
						<GraphAppearancePanel
							className="graph-appearance-panel--inline"
							controls={scene.typeControls}
							expanded={resolvedAppearancePanelOpen}
							onExpandedChange={setResolvedAppearancePanelOpen}
							onToggleTypeLabels={(typeId) => setResolvedTypeVisualConfig((currentConfig) => updateGraphNodeTypeLabelsVisible(currentConfig, typeId))}
							onTypeBrightnessChange={(typeId, brightness) =>
								setResolvedTypeVisualConfig((currentConfig) => updateGraphNodeTypeBrightness(currentConfig, typeId, brightness))
							}
							onTypeColorChange={(typeId, colorHex) =>
								setResolvedTypeVisualConfig((currentConfig) => updateGraphNodeTypeColor(currentConfig, typeId, colorHex))
							}
							panelId="graph-scene-3d-appearance-panel"
						/>
						<button className="hud-subtle-button graph-scene-3d__overview-button" onClick={handleResetOverview} type="button">
							{t('returnToFullMap')}
						</button>
					</div>
					<div className="graph-scene-3d__shortcut-list">
						<p className="hud-hint graph-scene-3d__shortcut-item">{t('workspace.mode.3dNavigateHint')}</p>
						<p className="hud-hint graph-scene-3d__shortcut-item">{t('workspace.mode.3dFlyHint')}</p>
					</div>
					{scene.placementGuide ? (
						<div className="graph-scene-3d__placement-card">
							<span className="graph-scene-3d__placement-state">{t('workspace.mode.3dPlacementActive')}</span>
							<strong>{formatMessage(t('workspace.mode.3dPlacementNode'), { name: scene.placementGuide.displayName })}</strong>
							<div className="graph-scene-3d__placement-meta">
								<span className="graph-scene-3d__meta-chip">{formatMessage(t('workspace.mode.3dPlacementDepth'), { depth: String(scene.placementGuide.depthValue) })}</span>
								<span className="graph-scene-3d__meta-chip">{formatMessage(t('workspace.mode.3dPlacementOffset'), { x: String(scene.placementGuide.deltaX), y: String(scene.placementGuide.deltaY), z: String(scene.placementGuide.deltaZ) })}</span>
							</div>
						</div>
					) : null}
					{resolvedInteractionMode === 'move-node' ? (
						<p className="hud-hint graph-scene-3d__drag-hint">{t('workspace.mode.3dMoveNodeHint')}</p>
					) : null}
				</section>
			</div>
		</section>
	);
}

type FlythroughKey = 'forward' | 'backward' | 'left' | 'right' | 'up' | 'down' | 'boost';

function normalizeFlythroughKey(key: string): FlythroughKey | null {
	switch (key.toLowerCase()) {
		case 'w':
		case 'arrowup':
			return 'forward';
		case 's':
		case 'arrowdown':
			return 'backward';
		case 'a':
		case 'arrowleft':
			return 'left';
		case 'd':
		case 'arrowright':
			return 'right';
		case 'e':
			return 'up';
		case 'q':
			return 'down';
		case 'shift':
			return 'boost';
		default:
			return null;
	}
}

function normalizePointerButton(button: number | undefined): number {
	if (typeof button !== 'number' || button < 0) {
		return 0;
	}

	return button;
}

function resolveFlythroughMovement(keys: Set<FlythroughKey>) {
	const forward = Number(keys.has('forward')) - Number(keys.has('backward'));
	const right = Number(keys.has('right')) - Number(keys.has('left'));
	const up = Number(keys.has('up')) - Number(keys.has('down'));

	if (forward === 0 && right === 0 && up === 0) {
		return null;
	}

	return {
		forward,
		right,
		up,
		boost: keys.has('boost'),
	};
}

function isKeyboardFlyBlocked(): boolean {
	if (typeof document === 'undefined') {
		return false;
	}

	const activeElement = document.activeElement;
	if (!(activeElement instanceof HTMLElement)) {
		return false;
	}

	if (activeElement.isContentEditable) {
		return true;
	}

	return ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON', 'A'].includes(activeElement.tagName);
}

function blurActiveElementSafely() {
	if (typeof document === 'undefined') {
		return;
	}

	if (document.activeElement instanceof HTMLElement) {
		document.activeElement.blur();
	}
}

function formatMessage(template: string, values: Record<string, string>): string {
	return Object.entries(values).reduce((message, [key, value]) => message.replace(`{${key}}`, value), template);
}

function createNumericRange(values: number[]): NumericRange {
	if (values.length === 0) {
		return { minimum: 0, maximum: 0 };
	}

	return values.reduce<NumericRange>(
		(accumulator, value) => ({
			minimum: Math.min(accumulator.minimum, value),
			maximum: Math.max(accumulator.maximum, value),
		}),
		{ minimum: Number.POSITIVE_INFINITY, maximum: Number.NEGATIVE_INFINITY },
	);
}

function normalizeRangeValue(value: number, range: NumericRange): number {
	if (!Number.isFinite(value)) {
		return 0.5;
	}

	if (!Number.isFinite(range.minimum) || !Number.isFinite(range.maximum) || range.minimum === range.maximum) {
		return 0.5;
	}

	return clamp((value - range.minimum) / (range.maximum - range.minimum), 0, 1);
}

function labelScaleForDistance(cameraDistance: number, range: NumericRange, isFocused: boolean): number {
	const distanceProgress = normalizeRangeValue(cameraDistance, range);
	const baseScale = clamp(1.08 - distanceProgress * 0.62, 0.42, 1.08);

	return isFocused ? clamp(baseScale + 0.14, 0.62, 1.18) : baseScale;
}

function labelOpacityForDistance(cameraDistance: number, range: NumericRange, isFocused: boolean): number {
	const distanceProgress = normalizeRangeValue(cameraDistance, range);
	const baseOpacity = clamp(0.94 - distanceProgress * 0.62, 0.14, 0.94);

	return isFocused ? 1 : baseOpacity;
}

function selectVisibleSceneLabelNodeIds(
	nodes: ProjectedNode[],
	viewport: { width: number; height: number },
	typeVisualConfig: Record<string, GraphNodeTypeVisualConfig>,
): Set<string> {
	const visibleNodeIds = new Set<string>();
	const acceptedBounds: SceneLabelBounds[] = [];
	const candidates = [...nodes]
		.filter(
			(node) => isTypeLabelCandidate(node, typeVisualConfig)
				&& isProjectedLabelInViewport(node, viewport)
				&& (node.isFocused || node.labelOpacity > 0.16),
		)
		.sort((left, right) => {
			if (left.isFocused !== right.isFocused) {
				return Number(right.isFocused) - Number(left.isFocused);
			}

			if (left.cameraDistance !== right.cameraDistance) {
				return left.cameraDistance - right.cameraDistance;
			}

			return right.labelScale - left.labelScale;
		});

	for (const candidate of candidates) {
		const bounds = approximateSceneLabelBounds(candidate, viewport);
		if (!candidate.isFocused && acceptedBounds.some((accepted) => sceneLabelBoundsOverlap(bounds, accepted, 8))) {
			continue;
		}

		visibleNodeIds.add(candidate.nodeId);
		acceptedBounds.push(bounds);
	}

	return visibleNodeIds;
}

function isTypeLabelCandidate(
	node: Pick<ProjectedNode, 'type' | 'isFocused'>,
	typeVisualConfig: Record<string, GraphNodeTypeVisualConfig>,
): boolean {
	if (node.isFocused) {
		return true;
	}

	return typeVisualConfig[node.type]?.labelsVisible ?? true;
}

function isProjectedLabelInViewport(
	node: Pick<ProjectedNode, 'clipZ' | 'labelX' | 'labelY'>,
	viewport: { width: number; height: number },
): boolean {
	const padding = 40;

	return node.clipZ >= -1 && node.clipZ <= 1
		&& node.labelX >= -padding
		&& node.labelX <= viewport.width + padding
		&& node.labelY >= -padding
		&& node.labelY <= viewport.height + padding;
}

function approximateSceneLabelBounds(
	node: Pick<ProjectedNode, 'displayName' | 'labelScale' | 'labelX' | 'labelY'>,
	viewport: { width: number; height: number },
): SceneLabelBounds {
	const baseWidth = clamp(42 + node.displayName.length * 16, 78, Math.max(120, Math.min(176, viewport.width - 32)));
	const width = baseWidth * clamp(node.labelScale, 0.42, 1.18);
	const height = clamp(26 * node.labelScale + 10, 18, 36);

	return {
		left: node.labelX - width / 2,
		right: node.labelX + width / 2,
		top: node.labelY - height / 2,
		bottom: node.labelY + height / 2,
	};
}

function sceneLabelBoundsOverlap(first: SceneLabelBounds, second: SceneLabelBounds, padding: number): boolean {
	return first.left < second.right + padding
		&& first.right > second.left - padding
		&& first.top < second.bottom + padding
		&& first.bottom > second.top - padding;
}


function drawSceneConnection(context: CanvasRenderingContext2D, connection: ProjectedConnection) {
	const dimmed = connection.emphasis === 'dimmed';
	const connectionOpacity = connection.highlighted
		? clamp((connection.source.opacity + connection.target.opacity) * 0.16, 0.18, 0.32)
		: dimmed
			? clamp((connection.source.opacity + connection.target.opacity) * 0.008, 0.008, 0.018)
			: clamp((connection.source.opacity + connection.target.opacity) * 0.045, 0.04, 0.085);
	const lineWidth = connection.highlighted ? 1.15 : dimmed ? 0.4 : 0.65;
	const stroke = sceneConnectionStrokeStyle(connection, connectionOpacity, dimmed);

	context.save();
	context.beginPath();
	context.moveTo(connection.source.x, connection.source.y);
	context.lineTo(connection.target.x, connection.target.y);
	context.strokeStyle = stroke;
	context.lineWidth = lineWidth;
	context.stroke();
	context.restore();
}

function drawSceneRelationshipEdge(context: CanvasRenderingContext2D, edge: ProjectedRelationshipEdge) {
	const connectionOpacity = edge.edgeKind === 'explicit-only'
		? clamp((edge.source.opacity + edge.target.opacity) * 0.12, 0.18, 0.28)
		: clamp((edge.source.opacity + edge.target.opacity) * 0.11, 0.17, 0.26);
	const stroke = `rgba(255, 195, 117, ${connectionOpacity})`;

	context.save();
	context.beginPath();
	if (edge.edgeKind === 'explicit-only') {
		context.setLineDash([10, 8]);
	}
	context.moveTo(edge.source.x, edge.source.y);
	context.lineTo(edge.target.x, edge.target.y);
	context.strokeStyle = stroke;
	context.lineWidth = edge.edgeKind === 'explicit-only' ? 1.35 : 1.05;
	context.stroke();
	context.setLineDash([]);
	context.restore();
}

function sceneConnectionStrokeStyle(connection: ProjectedConnection, connectionOpacity: number, dimmed: boolean): string {
	if (connection.reasonCode === 'shared-tags') {
		return connection.highlighted
			? `rgba(109, 221, 255, ${connectionOpacity * 2.65})`
			: `rgba(109, 221, 255, ${connectionOpacity * (dimmed ? 0.68 : 1.1)})`;
	}

	return connection.highlighted
		? `rgba(255, 255, 255, ${connectionOpacity * 3})`
		: `rgba(255, 255, 255, ${connectionOpacity * (dimmed ? 0.55 : 0.8)})`;
}

function drawSceneNode(context: CanvasRenderingContext2D, node: ProjectedNode) {
	const palette = sceneNodePalette(node);
	const haloRadius = node.radius * (node.isFocused ? 2.8 : node.haloStrength === 'soft' ? 2.05 : 1.75);
	const halo = context.createRadialGradient(node.x, node.y, node.radius * 0.12, node.x, node.y, haloRadius);
	halo.addColorStop(0, node.isFocused ? palette.focusGlow : palette.glow);
	halo.addColorStop(1, 'rgba(99, 230, 255, 0)');

	context.save();
	context.globalAlpha = node.opacity;
	context.fillStyle = halo;
	context.beginPath();
	context.arc(node.x, node.y, haloRadius, 0, Math.PI * 2);
	context.fill();

	const orb = context.createRadialGradient(
		node.x - node.radius * 0.34,
		node.y - node.radius * 0.42,
		node.radius * 0.16,
		node.x,
		node.y,
		node.radius,
	);
	orb.addColorStop(0, palette.highlight);
	orb.addColorStop(0.3, palette.mid);
	orb.addColorStop(0.72, palette.deep);
	orb.addColorStop(1, palette.shadow);

	context.fillStyle = orb;
	context.beginPath();
	context.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
	context.fill();

	context.strokeStyle = node.isFocused ? palette.focusRim : palette.rim;
	context.lineWidth = node.isFocused ? 2.2 : 1.1;
	context.beginPath();
	context.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
	context.stroke();
	context.restore();
}

function drawPlacementGuide(context: CanvasRenderingContext2D, guide: PlacementGuide | null) {
	if (!guide) {
		return;
	}

	context.save();
	context.setLineDash([8, 8]);
	context.strokeStyle = 'rgba(122, 223, 236, 0.64)';
	context.lineWidth = 1.2;
	context.beginPath();
	context.moveTo(guide.startX, guide.startY);
	context.lineTo(guide.currentX, guide.currentY);
	context.stroke();
	context.setLineDash([]);

	context.strokeStyle = 'rgba(124, 200, 214, 0.28)';
	context.lineWidth = 1;
	context.beginPath();
	context.arc(guide.startX, guide.startY, guide.currentRadius * 0.9, 0, Math.PI * 2);
	context.stroke();

	const landingShadow = context.createRadialGradient(
		guide.currentX,
		guide.currentY + guide.currentRadius * 0.9,
		guide.currentRadius * 0.24,
		guide.currentX,
		guide.currentY + guide.currentRadius * 0.9,
		guide.currentRadius * 1.8,
	);
	landingShadow.addColorStop(0, 'rgba(129, 218, 236, 0.22)');
	landingShadow.addColorStop(1, 'rgba(129, 218, 236, 0)');
	context.fillStyle = landingShadow;
	context.beginPath();
	context.ellipse(guide.currentX, guide.currentY + guide.currentRadius * 0.9, guide.currentRadius * 1.34, guide.currentRadius * 0.52, 0, 0, Math.PI * 2);
	context.fill();

	context.strokeStyle = 'rgba(186, 236, 246, 0.82)';
	context.lineWidth = 1.8;
	context.beginPath();
	context.arc(guide.currentX, guide.currentY, guide.currentRadius * 1.28, 0, Math.PI * 2);
	context.stroke();

	context.strokeStyle = 'rgba(140, 214, 230, 0.56)';
	context.lineWidth = 1;
	context.beginPath();
	context.arc(guide.currentX, guide.currentY, guide.currentRadius * 1.7, 0, Math.PI * 2);
	context.stroke();
	context.restore();
}

function createPlacementGuide(
	placementSession: NodePlacementSession,
	camera: { updateMatrixWorld(): void },
	viewport: { width: number; height: number },
	projectedMap: Record<string, ProjectedNode>,
): PlacementGuide | null {
	if (placementSession.status !== 'draft') {
		return null;
	}

	const currentNode = projectedMap[placementSession.nodeId];
	if (!currentNode) {
		return null;
	}

	const startPoint = worldPointFromAnchor(placementSession.startAnchor).project(camera as never);

	return {
		nodeId: placementSession.nodeId,
		displayName: currentNode.displayName,
		startX: (startPoint.x * 0.5 + 0.5) * viewport.width,
		startY: (-startPoint.y * 0.5 + 0.5) * viewport.height,
		currentX: currentNode.x,
		currentY: currentNode.y,
		currentRadius: currentNode.radius,
		depthValue: Math.round(placementSession.draftAnchor.z),
		deltaX: Math.round(placementSession.draftAnchor.x - placementSession.startAnchor.x),
		deltaY: Math.round(placementSession.draftAnchor.y - placementSession.startAnchor.y),
		deltaZ: Math.round(placementSession.draftAnchor.z - placementSession.startAnchor.z),
	};
}

function sceneNodePalette(node: ProjectedNode): {
	glow: string;
	focusGlow: string;
	highlight: string;
	mid: string;
	deep: string;
	shadow: string;
	rim: string;
	focusRim: string;
} {
	const brightness = node.visualBrightness;
	const brightAlpha = clamp(0.2 + brightness * 0.22, 0.18, 0.54);
	const midAlpha = clamp(0.16 + brightness * 0.18, 0.14, 0.4);
	const deepAlpha = clamp(0.3 + brightness * 0.28, 0.28, 0.74);
	const shadowAlpha = clamp(0.56 + brightness * 0.22, 0.52, 0.9);
	const rimAlpha = clamp(0.18 + brightness * 0.22, 0.16, 0.48);
	const focusRimAlpha = clamp(0.64 + brightness * 0.26, 0.64, 1);
	const nodeColor = node.visualColorHex;
	const whiteHighlight = mixHexColors(nodeColor, '#ffffff', 0.52);
	const midColor = mixHexColors(nodeColor, '#d7ebff', 0.28);
	const deepColor = mixHexColors(nodeColor, '#0a1020', 0.6);
	const shadowColor = mixHexColors(nodeColor, '#03060d', 0.78);
	const glowColor = mixHexColors(nodeColor, '#ffffff', 0.18);

	if (node.isFocused) {
		return {
			glow: toRgba(glowColor, clamp(brightAlpha * 0.6, 0.18, 0.42)),
			focusGlow: toRgba(whiteHighlight, clamp(brightAlpha * 1.5, 0.44, 0.84)),
			highlight: toRgba(whiteHighlight, clamp(0.74 + brightness * 0.16, 0.76, 1)),
			mid: toRgba(midColor, clamp(midAlpha * 1.35, 0.28, 0.72)),
			deep: toRgba(deepColor, clamp(deepAlpha * 1.12, 0.42, 0.9)),
			shadow: toRgba(shadowColor, clamp(shadowAlpha, 0.6, 0.96)),
			rim: toRgba(whiteHighlight, clamp(rimAlpha * 1.36, 0.34, 0.74)),
			focusRim: toRgba('#ffffff', focusRimAlpha),
		};
	}
	return {
		glow: toRgba(glowColor, clamp(brightAlpha * 0.52, 0.14, 0.32)),
		focusGlow: toRgba(whiteHighlight, clamp(brightAlpha, 0.28, 0.54)),
		highlight: toRgba(whiteHighlight, clamp(0.42 + brightness * 0.2, 0.4, 0.84)),
		mid: toRgba(midColor, clamp(midAlpha, 0.18, 0.46)),
		deep: toRgba(deepColor, clamp(deepAlpha, 0.34, 0.82)),
		shadow: toRgba(shadowColor, clamp(shadowAlpha, 0.54, 0.94)),
		rim: toRgba(whiteHighlight, rimAlpha),
		focusRim: toRgba('#ffffff', focusRimAlpha),
	};
}


function pickProjectedNode(nodes: ProjectedNode[], x: number, y: number): ProjectedNode | null {
	for (const node of [...nodes].reverse()) {
		if (Math.hypot(node.x - x, node.y - y) <= node.radius + 6) {
			return node;
		}
	}

	return null;
}

function pickFocusedPlacementNode(
	nodes: ProjectedNode[],
	focusedNodeId: string | null,
	x: number,
	y: number,
): ProjectedNode | null {
	if (!focusedNodeId) {
		return null;
	}

	const focusedNode = nodes.find((node) => node.nodeId === focusedNodeId);
	if (!focusedNode) {
		return null;
	}

	return Math.hypot(focusedNode.x - x, focusedNode.y - y) <= focusedNode.radius + 56
		? focusedNode
		: null;
}

function anchorFromScreenPoint(
	screenX: number,
	screenY: number,
	camera: PerspectiveCamera,
	viewport: { width: number; height: number },
	planeAnchor: GraphNodeAnchor,
): GraphNodeAnchor {
	camera.updateMatrixWorld();

	const pointOnRay = new Vector3(
		(screenX / viewport.width) * 2 - 1,
		-((screenY / viewport.height) * 2 - 1),
		0.5,
	);
	pointOnRay.unproject(camera);

	const rayOrigin = camera.position.clone();
	const rayDirection = pointOnRay.sub(rayOrigin).normalize();
	const planePoint = worldPointFromAnchor(planeAnchor);
	const planeNormal = new Vector3();
	camera.getWorldDirection(planeNormal);

	const denominator = planeNormal.dot(rayDirection);
	const distance = Math.abs(denominator) > 0.000001
		? planeNormal.dot(planePoint.sub(rayOrigin)) / denominator
		: 0;
	const point = rayOrigin.add(rayDirection.multiplyScalar(distance));

	return {
		x: point.x,
		y: -point.y,
		z: point.z,
	};
}

function fallbackAnchor(index: number, nodeCount: number): GraphNodeAnchor {
	const fallbackRing = 220 + (index % 4) * 72;
	const fallbackAngle = (index / Math.max(nodeCount, 1)) * Math.PI * 2;

	return {
		x: Math.cos(fallbackAngle) * fallbackRing,
		y: Math.sin(fallbackAngle) * fallbackRing,
		z: ((index % 7) - 3) * 48,
	};
}

function getCanvas2DContext(canvas: HTMLCanvasElement | null): CanvasRenderingContext2D | null {
	if (!canvas) {
		return null;
	}

	try {
		return canvas.getContext('2d');
	} catch {
		return null;
	}
}

function isPointInViewport(x: number, y: number, viewport: { width: number; height: number }): boolean {
	return x >= -120 && x <= viewport.width + 120 && y >= -80 && y <= viewport.height + 80;
}

function applyAnchorDrift(
	anchor: GraphNodeAnchor,
	nodeId: string,
	index: number,
	timeSec: number,
): GraphNodeAnchor {
	const seed = hashNode3D(nodeId) + index * 17;
	const amplitude = 4;
	const speed = 0.18;
	const phase = (seed % 360) * (Math.PI / 180);
	const driftX = Math.sin(timeSec * speed + phase) * amplitude;
	const driftY = Math.cos(timeSec * (speed * 0.75) + phase * 1.2) * amplitude * 0.7;
	const driftZ = Math.sin(timeSec * (speed * 0.5) + phase * 0.8) * amplitude * 0.5;

	return {
		x: anchor.x + driftX,
		y: anchor.y + driftY,
		z: anchor.z + driftZ,
	};
}

function hashNode3D(value: string): number {
	let hash = 0;
	for (let index = 0; index < value.length; index += 1) {
		hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
	}
	return hash;
}

function clamp(value: number, minimum: number, maximum: number): number {
	return Math.min(maximum, Math.max(minimum, value));
}
